import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type { CreatePdfTemplateInput, PdfTemplateRow, UpdatePdfTemplateInput } from '@erp/data-access/interfaces';

const KEY = ['pdf-templates'] as const;

export function usePdfTemplates() {
  const ds = useDataService();
  return useQuery({
    queryKey: KEY,
    queryFn: () => ds.pdfTemplates.list(),
  });
}

export function usePdfTemplate(id: string | undefined) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...KEY, id],
    queryFn: () => ds.pdfTemplates.getById(id!),
    enabled: !!id,
    placeholderData: () => (id ? listCacheFallback<PdfTemplateRow>(qc, KEY, id) : undefined),
  });
}

export function useCreatePdfTemplate() {
  const ds = useDataService();
  return useResilientMutation<CreatePdfTemplateInput, PdfTemplateRow>({
    entity: 'pdf_templates',
    op: 'create',
    invalidate: [KEY],
    online: (input) => ds.pdfTemplates.create(input),
    optimistic: (input, clientId) => ({ id: clientId, ...input, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }) as PdfTemplateRow,
    applyOptimistic: (qc, row) => {
      qc.setQueriesData<PdfTemplateRow[]>({ queryKey: KEY }, (old) =>
        Array.isArray(old) && !old.some((r) => r.id === row.id) ? [row, ...old] : old,
      );
    },
  });
}

export function useUpdatePdfTemplate() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdatePdfTemplateInput }, PdfTemplateRow>({
    entity: 'pdf_templates',
    op: 'update',
    invalidate: [KEY],
    online: ({ id, input }) => ds.pdfTemplates.update(id, input),
    optimistic: ({ id, input }) => ({ id, ...input, updatedAt: new Date().toISOString() }) as PdfTemplateRow,
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<PdfTemplateRow[]>({ queryKey: KEY }, (old) =>
        Array.isArray(old) ? old.map((r) => (r.id === id ? ({ ...r, ...input } as PdfTemplateRow) : r)) : old,
      );
    },
  });
}

export function useDeletePdfTemplate() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'pdf_templates',
    op: 'delete',
    invalidate: [KEY],
    online: (id) => ds.pdfTemplates.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<PdfTemplateRow[]>({ queryKey: KEY }, (old) =>
        Array.isArray(old) ? old.filter((r) => r.id !== id) : old,
      );
      qc.removeQueries({ queryKey: [...KEY, id] });
    },
  });
}
